import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { Field, ErrorMessage, useField } from 'formik';
import { useId } from '@reach/auto-id';

import Typography from '../../atoms/typography';

const errorStyles = (props) => css`
  border-color: ${props.theme.color.brandError};
  color: ${props.theme.color.brandError};
`;

const InputField = styled(Field)`
  appearance: none;
  background: ${(props) => props.theme.color.brandNeutral};
  border: 0 none;
  box-sizing: border-box;
  color: ${(props) => props.theme.color.brand};
  font-family: ${(props) => props.theme.font.sans};
  font-size: ${(props) => props.theme.modularScale.small};
  height: 10rem;
  letter-spacing: 0.04em;
  outline-color: ${(props) => props.theme.color.brandLight};
  outline: 0;
  padding: 1.1rem 1.5rem;
  resize: vertical;
  width: 100%;

  ::placeholder {
    color: ${(props) => props.theme.color.brandLight};
  }

  :-internal-autofill-selected {
    /* TODO: go away Google! */
    color: ${(props) => props.theme.color.brand} !important;
  }

  & ~ & {
    margin-top: 1rem;
  }
`;

const ErrorMsg = styled(ErrorMessage)`
  color: ${(props) => props.theme.color.brandError};
  display: block;
  font-family: ${(props) => props.theme.font.sans};
  font-size: 1.2rem;
  left: 0;
  margin-top: 0.25rem;
  position: absolute;
  top: 100%;
  width: 100%;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column-reverse;
  position: relative;

  ${ErrorMsg} + ${InputField} {
    border: 0.2rem solid transparent;
    ${errorStyles}

    ::placeholder {
      color: ${(props) => props.theme.color.brandError};
      opacity: 0.6;
    }
  }
`;

const Label = styled(Typography)`
  color: ${(props) => props.theme.color.brand};
  margin-bottom: 0.5rem;
`;

const FormikField = forwardRef((props, ref) => {
  const meta = useField(props.name)[1];

  return (
    <>
      {
        props.label ? (
          <Wrapper className={props.className}>
            <ErrorMsg id={`${props.id}-error`} role="alert" name={props.name} component="span" />
            <InputField
              innerRef={ref}
              id={props.id || `${props.label.toLowerCase().split(' ').join('-')}-${useId()}`}
              name={props.name}
              placeholder={props.placeholder}
              validate={props.validate}
              component="textarea"
              aria-describedby={meta.touched && meta.error ? `${props.id}-error` : null}
            />

            {
              props.label && (
                <Label element="label" like="label-1" htmlFor={props.id || `${props.label.toLowerCase().split(' ').join('-')}-${useId()}`}>
                  {props.label}
                </Label>
              )
            }
          </Wrapper>
        ) : null
      }
    </>
  );
});

FormikField.defaultProps = {
  className: '',
  id: '',
  validate: () => { }
};

FormikField.propTypes = {
  className: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  validate: PropTypes.func
};

export default FormikField;
